/**
 * Type guard utilities for Axios error handling
 * Provides type-safe error handling without using 'any'
 */
import { AxiosError } from "axios";

/**
 * Type guard to check if an error is an AxiosError
 * @param error - The error to check
 * @returns True if the error is an AxiosError
 */
export function isAxiosError<T = unknown>(
  error: unknown
): error is AxiosError<T> {
  return (
    typeof error === "object" &&
    error !== null &&
    "isAxiosError" in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Extracts error message from an unknown error
 * @param error - The error to extract message from
 * @returns The error message string
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return "An unknown error occurred";
}

/**
 * Handles API errors in a type-safe manner
 * Extracts response data from Axios errors or returns the original error
 * @param error - The error to handle
 * @returns The response data if available, otherwise the error
 */
export function handleApiError(error: unknown): unknown {
  if (isAxiosError(error)) {
    return error.response?.data || error;
  }
  return error;
}
